import { ProjectTimeEntry, TimberProject, ProjectPhase, isEarlyFunnelProjectPhase } from '../types';

/** Resolved billing phase for a time entry — never uses the project's *current* phase as a proxy. */
export type ResolvedRecordedPhase = ProjectPhase | 'unknown';

function startOfLocalDayMs(d: Date): number {
  const x = new Date(d);
  return new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
}

export type ResolveRecordedPhaseOptions = {
  /**
   * First day the project counts as "under contract" for date rules: `contractedAt`, proposal/deal close, etc.
   * Usually from `TimberProject.contractedAt` or deal `closedAt` when the former is missing.
   */
  phaseCutoverDate?: Date | null;
  /** Task group name (e.g. "Preconstruction") — last-resort hint for legacy entries without stored phase. */
  taskGroupName?: string | null;
};

/**
 * Prefer stored `entry.phase`. Else compare work date to `phaseCutoverDate` (or `project.contractedAt`).
 * If still ambiguous: project still in preconstruction → precon; else task group name hint; else unknown.
 */
export function resolveRecordedPhase(
  entry: ProjectTimeEntry,
  proj?: TimberProject | null,
  options?: ResolveRecordedPhaseOptions,
): ResolvedRecordedPhase {
  if (entry.phase === 'preconstruction' || entry.phase === 'contracted') return entry.phase;

  const cutoverRaw = proj?.contractedAt ?? options?.phaseCutoverDate ?? undefined;
  if (cutoverRaw) {
    const work = startOfLocalDayMs(new Date(entry.date));
    const cut = startOfLocalDayMs(new Date(cutoverRaw));
    return work < cut ? 'preconstruction' : 'contracted';
  }

  if (isEarlyFunnelProjectPhase(proj?.phase)) return 'preconstruction';

  const gn = options?.taskGroupName?.trim() || '';
  if (/preconstruction|pre-construction|pre\s*con/i.test(gn)) return 'preconstruction';

  return 'unknown';
}

export function recordedPhaseLabel(p: ResolvedRecordedPhase): 'Precon' | 'Contract' | 'Unknown' {
  if (p === 'preconstruction') return 'Precon';
  if (p === 'contracted') return 'Contract';
  return 'Unknown';
}
